/**
 * Funções puras de métricas para Desempenho de Atendentes (período filtrado).
 */

import {
  fimNaFaixaBr,
  filterResolvidosFimNaFaixaBr,
  resolvidoPorBot,
  resolvidoPorAtendenteHumano,
  inicioNaFaixaBr,
} from '../operacao-central/operacao-central-utils'

export function isResolvidoStatus(at) {
  if (!at || at.status == null) return false
  return String(at.status).trim().toLowerCase() === 'resolvido'
}

/**
 * Atendimento "iniciado" pelo atendente da linha:
 * atendente_ativo != null, atendente_ativo.id definido, fim_bot nulo/vazio, atendimento_bot === false,
 * e o id do registro coincide com o id da linha (bot / sem_atendente não contam).
 */
function atendimentoIniciadoPeloAtendenteNaLinha(at, idLinha) {
  if (!at || idLinha === 'bot' || idLinha === 'sem_atendente') return false
  if (at.atendimento_bot !== false) return false
  if (at.fim_bot != null && at.fim_bot !== '') return false
  const aa = at.atendente_ativo
  if (aa == null) return false
  const idAtt = aa.id
  if (idAtt == null || idAtt === '') return false
  return String(idAtt) === String(idLinha)
}

export function calcularTempoPrimeiraRespostaMin(at) {
  const primeiraRespostaEm = at.primeira_mensagem_atendente_em ? new Date(at.primeira_mensagem_atendente_em).getTime() : null
  if (primeiraRespostaEm == null || Number.isNaN(primeiraRespostaEm)) return null
  const usoInicio = at.atendimento_bot === false && (at.fim_bot == null || at.fim_bot === undefined)
  const ref = usoInicio
    ? (at.inicio ? new Date(at.inicio).getTime() : null)
    : (at.fim_bot ? new Date(at.fim_bot).getTime() : null)
  if (ref == null || Number.isNaN(ref)) return null
  return (primeiraRespostaEm - ref) / 1000 / 60
}

export function refPrimeiraResposta(at) {
  const usoInicio = at.atendimento_bot === false && (at.fim_bot == null || at.fim_bot === undefined)
  if (usoInicio && at.inicio) return new Date(at.inicio).getTime()
  if (at.fim_bot) return new Date(at.fim_bot).getTime()
  return null
}

/**
 * Minutos entre dois timestamps que caem dentro de horario_funcionamento.
 */
export function minutosDentroDoHorario(inicioTimestamp, fimTimestamp, horarioFuncionamento) {
  if (!inicioTimestamp || !fimTimestamp || !horarioFuncionamento || !horarioFuncionamento.length) return null
  const inicio = new Date(inicioTimestamp)
  const fim = new Date(fimTimestamp)
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime()) || inicio >= fim) return 0
  let totalMin = 0
  const dayStart = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate(), 0, 0, 0, 0)
  const dayEnd = new Date(fim.getFullYear(), fim.getMonth(), fim.getDate(), 23, 59, 59, 999)
  let d = new Date(dayStart)
  while (d <= dayEnd) {
    const dia = d.getDay()
    const year = d.getFullYear()
    const month = d.getMonth()
    const date = d.getDate()
    const intervalos = horarioFuncionamento.filter(h => h.dia === dia)
    for (let i = 0; i < intervalos.length; i++) {
      const int = intervalos[i]
      const [abreH, abreM] = (int.abre || '00:00').split(':').map(Number)
      const [fechaH, fechaM] = (int.fecha || '23:59').split(':').map(Number)
      const intervalStart = new Date(year, month, date, abreH || 0, abreM || 0, 0, 0)
      const intervalEnd = new Date(year, month, date, fechaH || 23, fechaM || 59, 59, 999)
      const overlapStart = new Date(Math.max(inicio.getTime(), intervalStart.getTime()))
      const overlapEnd = new Date(Math.min(fim.getTime(), intervalEnd.getTime()))
      if (overlapStart < overlapEnd) {
        totalMin += (overlapEnd.getTime() - overlapStart.getTime()) / 60000
      }
    }
    d.setDate(d.getDate() + 1)
  }
  return totalMin
}

/**
 * Monta linhas agregadas por atendente/bot a partir de resolvidos.
 * sem_atendente: volume por início BR (lista `resolvidos` filtrada pelo caller) — alinhado ao card
 * "Sem atendente (início no período)" na OC.
 * Bot e humanos: fim civil BR no período sobre `resolvidosPeriodoBruto`, deduplicado por atendimento_id,
 * mesma base dos cards "Finalizados pelo bot" e "Finalizados por atendente humano" na OC.
 * Se `resolvidosPeriodoBruto` ou faixa de fim faltar, bot e humanos voltam ao loop único com `resolvidos`.
 * `atendimentosAbertos`: fila atual (getAttendings); soma em `numero_conversas` quando início (BR) está no período
 * e o ticket ainda não foi contado em `bump` (ex.: em andamento, listado na API de resolvidos mas sem fim no período).
 */
export function buildTabelaAtendentesRows(
  resolvidos,
  mapaAtendentes,
  horario,
  considerarHorarioAtendimento,
  isoFimInicioBr = null,
  isoFimFimBr = null,
  resolvidosPeriodoBruto = null,
  atendimentosAbertos = null,
) {
  const metricasPorAtendente = {}
  const addAtendente = (id, nome) => {
    if (metricasPorAtendente[id]) return
    metricasPorAtendente[id] = {
      id,
      nome,
      numero_conversas: 0,
      conversas_iniciadas_pelo_atendente: 0,
      soma_primeira_resposta: 0,
      count_primeira_resposta: 0,
      soma_resolucao: 0,
      count_resolucao: 0,
      contagem_resolucao: 0,
      conversas: [],
    }
  }

  const considerarHorario = considerarHorarioAtendimento && horario.length > 0
  const usarFaixaFimBr = Boolean(isoFimInicioBr && isoFimFimBr)
  const finalizadosPorFimNaListaBruta =
    usarFaixaFimBr &&
    resolvidosPeriodoBruto != null &&
    Array.isArray(resolvidosPeriodoBruto)

  const idsJaContadosEmConversas = new Set()
  const chaveTicket = at => {
    if (!at) return null
    const x = at.atendimento_id != null ? at.atendimento_id : at.id
    return x != null && x !== '' ? String(x) : null
  }

  const acrescentaPrimeiraRespostaMetrica = (m, at) => {
    if (!at || !at.primeira_mensagem_atendente_em) return
    const ref = refPrimeiraResposta(at)
    if (ref == null) return
    const primeiraRespostaMin = considerarHorario
      ? minutosDentroDoHorario(ref, new Date(at.primeira_mensagem_atendente_em).getTime(), horario)
      : calcularTempoPrimeiraRespostaMin(at)
    if (primeiraRespostaMin != null && !Number.isNaN(primeiraRespostaMin)) {
      m.soma_primeira_resposta += primeiraRespostaMin
      m.count_primeira_resposta += 1
    }
  }

  const bump = (at, atendenteId, nome) => {
    addAtendente(atendenteId, nome)
    const m = metricasPorAtendente[atendenteId]
    m.numero_conversas += 1
    const k = chaveTicket(at)
    if (k != null) idsJaContadosEmConversas.add(k)
    if (atendimentoIniciadoPeloAtendenteNaLinha(at, atendenteId)) {
      m.conversas_iniciadas_pelo_atendente += 1
    }

    const fimAt = at.fim || at.data_resolucao || at.ultima_mensagem_em
    const fimContaResolucao = !usarFaixaFimBr || fimNaFaixaBr(at, isoFimInicioBr, isoFimFimBr)
    const inicioAt = at.inicio ? new Date(at.inicio).getTime() : null
    const fimAtMs = fimAt ? new Date(fimAt).getTime() : null
    let tempoResolucaoMin = null
    if (inicioAt && fimAtMs && fimContaResolucao) {
      tempoResolucaoMin = considerarHorario
        ? minutosDentroDoHorario(inicioAt, fimAtMs, horario)
        : (fimAtMs - inicioAt) / 1000 / 60
    }

    acrescentaPrimeiraRespostaMetrica(m, at)

    if (tempoResolucaoMin != null && !Number.isNaN(tempoResolucaoMin) && isResolvidoStatus(at)) {
      m.soma_resolucao += tempoResolucaoMin
      m.count_resolucao += 1
    }
    if (fimAt && fimContaResolucao && isResolvidoStatus(at)) m.contagem_resolucao += 1

    m.conversas.push({
      atendimento_id: at.atendimento_id || at.id,
      cliente_nome: at.cliente?.name || at.cliente_nome || 'Cliente',
      inicio: at.inicio,
      inicio_formatado: at.inicio ? new Date(at.inicio).toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      }) : '—',
      tempo_resolucao_min: tempoResolucaoMin,
    })
  }

  ;(resolvidos || []).forEach(at => {
    if (finalizadosPorFimNaListaBruta) {
      if (at.atendimento_bot === true) return
      const hid = at.atendente_ativo?.id
      if (hid != null && hid !== '') return
      bump(at, 'sem_atendente', 'Desconhecido')
      return
    }
    const atendenteId = at.atendimento_bot ? 'bot' : (at.atendente_ativo?.id ?? 'sem_atendente')
    const nome = at.atendimento_bot ? 'Bot' : (mapaAtendentes[at.atendente_ativo?.id] || 'Desconhecido')
    bump(at, atendenteId, nome)
  })

  if (finalizadosPorFimNaListaBruta) {
    const fimDedup = filterResolvidosFimNaFaixaBr(resolvidosPeriodoBruto, isoFimInicioBr, isoFimFimBr)
    fimDedup.filter(resolvidoPorBot).forEach(at => bump(at, 'bot', 'Bot'))
    fimDedup.filter(resolvidoPorAtendenteHumano).forEach(at => {
      const id = String(at.atendente_ativo.id)
      const nome = mapaAtendentes[at.atendente_ativo.id] || 'Desconhecido'
      bump(at, id, nome)
    })
  }

  if (
    Array.isArray(atendimentosAbertos) &&
    atendimentosAbertos.length &&
    isoFimInicioBr &&
    isoFimFimBr
  ) {
    const seenAberto = new Set()
    atendimentosAbertos.forEach(at => {
      if (!at || !inicioNaFaixaBr(at, isoFimInicioBr, isoFimFimBr)) return
      const aidKey = chaveTicket(at)
      if (!aidKey) return
      if (seenAberto.has(aidKey)) return
      seenAberto.add(aidKey)
      if (idsJaContadosEmConversas.has(aidKey)) return

      let atendenteId
      let nome
      if (at.atendimento_bot === true) {
        atendenteId = 'bot'
        nome = 'Bot'
      } else {
        const hid = at.atendente_ativo?.id
        if (hid != null && hid !== '') {
          atendenteId = String(hid)
          nome = mapaAtendentes[hid] || mapaAtendentes[String(hid)] || 'Desconhecido'
        } else {
          atendenteId = 'sem_atendente'
          nome = 'Desconhecido'
        }
      }
      addAtendente(atendenteId, nome)
      const m = metricasPorAtendente[atendenteId]
      m.numero_conversas += 1
      if (atendimentoIniciadoPeloAtendenteNaLinha(at, atendenteId)) {
        m.conversas_iniciadas_pelo_atendente += 1
      }
      m.conversas.push({
        atendimento_id: at.atendimento_id || at.id,
        cliente_nome: at.cliente?.name || at.cliente_nome || 'Cliente',
        inicio: at.inicio,
        inicio_formatado: at.inicio ? new Date(at.inicio).toLocaleString('pt-BR', {
          day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
        }) : '—',
        tempo_resolucao_min: null,
      })
      acrescentaPrimeiraRespostaMetrica(m, at)
      idsJaContadosEmConversas.add(aidKey)
    })
  }

  return Object.keys(metricasPorAtendente).map(id => {
    const m = metricasPorAtendente[id]
    return {
      id: m.id,
      nome: m.nome,
      numero_conversas: m.numero_conversas,
      conversas_iniciadas_pelo_atendente: m.conversas_iniciadas_pelo_atendente,
      tempo_medio_primeira_resposta: m.count_primeira_resposta ? m.soma_primeira_resposta / m.count_primeira_resposta : null,
      tempo_medio_resolucao: m.count_resolucao ? m.soma_resolucao / m.count_resolucao : null,
      contagem_resolucao: m.contagem_resolucao,
      count_primeira_resposta: m.count_primeira_resposta,
      count_resolucao_timeline: m.count_resolucao,
      _conversas: m.conversas,
    }
  })
}

/** KPIs globais a partir das linhas agregadas + totais brutos. */
export function summarizeKpisFromRows(rows, totalAtendimentosNoPeriodo) {
  const total = totalAtendimentosNoPeriodo || 0
  const botRow = rows.find(r => r.id === 'bot')
  const conversasBot = botRow ? botRow.numero_conversas : 0
  let somaResolucoes = 0
  let somaPR = 0
  let nPR = 0
  let somaRes = 0
  let nRes = 0
  rows.forEach(r => {
    somaResolucoes += r.contagem_resolucao || 0
    if (r.tempo_medio_primeira_resposta != null && r.count_primeira_resposta) {
      somaPR += r.tempo_medio_primeira_resposta * r.count_primeira_resposta
      nPR += r.count_primeira_resposta
    }
    if (r.tempo_medio_resolucao != null && r.count_resolucao_timeline) {
      somaRes += r.tempo_medio_resolucao * r.count_resolucao_timeline
      nRes += r.count_resolucao_timeline
    }
  })
  const tempoMedioPrimeira = nPR ? somaPR / nPR : null
  const tempoMedioResolucao = nRes ? somaRes / nRes : null
  const taxaResolucaoPct = total > 0 ? (somaResolucoes / total) * 100 : 0
  const pctAutomatizadoBot = total > 0 ? (conversasBot / total) * 100 : 0
  return {
    totalConversas: total,
    totalResolucoes: somaResolucoes,
    taxaResolucaoPct,
    pctAutomatizadoBot,
    tempoMedioPrimeira,
    tempoMedioResolucao,
  }
}

export function formatDeltaPct(current, previous) {
  if (previous == null || current == null || Number.isNaN(previous) || Number.isNaN(current)) return null
  if (previous === 0) return null
  const d = ((current - previous) / previous) * 100
  if (!Number.isFinite(d)) return null
  const rounded = Math.round(d * 10) / 10
  const sign = rounded > 0 ? '+' : ''
  return `${sign}${rounded}% vs período anterior`
}

/**
 * Métricas do "Resumo do período" a partir da lista `resolvidos` da API de histórico.
 */
export function kpiResumoContagens(resolvidos) {
  const list = Array.isArray(resolvidos) ? resolvidos : []
  const totalConversas = list.length
  const somenteResolvido = list.filter(isResolvidoStatus)
  const totalFinalizados = somenteResolvido.length

  const semAtendenteVinculado = somenteResolvido.filter(
    at => at.atendimento_bot === false && at.atendente_ativo == null,
  ).length
  const resolvidosPelaEquipe = somenteResolvido.filter(
    at => at.atendimento_bot === false && at.atendente_ativo != null,
  ).length
  const resolvidosPeloBot = somenteResolvido.filter(
    at => at.atendimento_bot === true && at.atendente_ativo == null,
  ).length

  const pctBot = totalFinalizados > 0 ? (resolvidosPeloBot / totalFinalizados) * 100 : 0
  const pctEquipe = totalFinalizados > 0 ? (resolvidosPelaEquipe / totalFinalizados) * 100 : 0

  return {
    totalConversas,
    totalFinalizados,
    semAtendenteVinculado,
    resolvidosPelaEquipe,
    resolvidosPeloBot,
    pctBot,
    pctEquipe,
  }
}

/**
 * Tempo médio de resolução apenas para itens com status "resolvido" e início/fim válidos.
 * Mesma lógica de minutos que em buildTabelaAtendentesRows (com ou sem horário de atendimento).
 */
export function computeTempoMedioResolucaoSomenteResolvidos(
  resolvidos,
  horario,
  considerarHorarioAtendimento,
) {
  const list = Array.isArray(resolvidos) ? resolvidos : []
  const considerarHorario = Boolean(
    considerarHorarioAtendimento && horario && horario.length > 0,
  )
  const minutosLista = list
    .filter(isResolvidoStatus)
    .map(atItem => {
      const fimRaw = atItem.fim || atItem.data_resolucao || atItem.ultima_mensagem_em
      const inicioAt = atItem.inicio ? new Date(atItem.inicio).getTime() : null
      const fimAtMs = fimRaw ? new Date(fimRaw).getTime() : null
      if (inicioAt == null || fimAtMs == null || Number.isNaN(inicioAt) || Number.isNaN(fimAtMs)) {
        return null
      }
      if (inicioAt >= fimAtMs) return null
      const tempoResolucaoMin = considerarHorario
        ? minutosDentroDoHorario(inicioAt, fimAtMs, horario)
        : (fimAtMs - inicioAt) / 1000 / 60
      if (tempoResolucaoMin == null || Number.isNaN(tempoResolucaoMin)) return null
      return tempoResolucaoMin
    })
    .filter(m => m != null)
  if (!minutosLista.length) return null
  return minutosLista.reduce((acc, mm) => acc + mm, 0) / minutosLista.length
}
